import { useEffect, useState } from "react";
import type { FieldContent } from "@capture/shared";
import { ALLOWED_CHARS_MODE_PRESETS } from "@capture/shared";
import { useFormConfigStore } from "../../state/store";
import { FieldRow, FieldCheckboxRow, blurOnEnter } from "./Field";

/**
 * Validation section — modal-redesign WU-C (2026-05-10).
 *
 * Hosts the 8 properties widened in WU-A:
 *   - isRequired (relocated from BehaviorSection)
 *   - minLength / maxLength — text only
 *   - minValue / maxValue — numeric (number / decimal / currency)
 *   - regexPattern + allowedCharsMode preset — text only
 *   - readOnly — always
 *
 * Sits between Identity and Display in the Basics tab. Conditional
 * rendering is per-dataType: numeric bounds for numeric types, text
 * bounds + regex for text, Required/readOnly always.
 *
 * `allowedCharsMode` is UI sugar: presets map to a regexPattern at
 * write time so the runtime validator only reads `regexPattern`.
 *
 * Inline errors:
 *   - min* > max* on either bound pair
 *   - regexPattern that fails to compile
 */

/** Preset → regexPattern at write time. `letters`/`alphanumeric`/`digits` come
 *  from the shared `ALLOWED_CHARS_MODE_PRESETS` single source; `any` clears the
 *  pattern; `custom` leaves the existing regexPattern untouched (handled below). */
const ALLOWED_CHARS_MODE_WRITE: Record<
  NonNullable<FieldContent["allowedCharsMode"]>,
  string | undefined
> = {
  any: undefined,
  ...ALLOWED_CHARS_MODE_PRESETS,
  custom: null as unknown as string,
};

const ALLOWED_CHARS_MODE_OPTIONS: ReadonlyArray<{
  value: NonNullable<FieldContent["allowedCharsMode"]>;
  label: string;
}> = [
  { value: "any", label: "Any" },
  { value: "letters", label: "Letters only (A–Z)" },
  { value: "alphanumeric", label: "Letters + digits" },
  { value: "digits", label: "Digits only (0–9)" },
  { value: "custom", label: "Custom regex" },
];

function tryCompileRegex(pattern: string): string | null {
  try {
    new RegExp(pattern);
    return null;
  } catch (e) {
    return (e as Error).message ?? "Invalid regex";
  }
}

function emptyToUndefinedNumber(s: string): number | undefined {
  if (s.trim() === "") return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

export function ValidationSection() {
  const content = useFormConfigStore((s) => s.content);
  const selectedFieldName = useFormConfigStore((s) => s.selectedFieldName);
  const updateField = useFormConfigStore((s) => s.updateField);

  const field = content?.fields.find((f) => f.name === selectedFieldName) ?? null;

  // Drafts for text/number inputs (commit on blur, like the rest of the
  // field-editor sections).
  const [minLengthDraft, setMinLengthDraft] = useState(
    field?.minLength != null ? String(field.minLength) : "",
  );
  const [maxLengthDraft, setMaxLengthDraft] = useState(
    field?.maxLength != null ? String(field.maxLength) : "",
  );
  const [minValueDraft, setMinValueDraft] = useState(
    field?.minValue != null ? String(field.minValue) : "",
  );
  const [maxValueDraft, setMaxValueDraft] = useState(
    field?.maxValue != null ? String(field.maxValue) : "",
  );
  const [regexDraft, setRegexDraft] = useState(field?.regexPattern ?? "");
  const [verifyThresholdDraft, setVerifyThresholdDraft] = useState(
    field?.verifyThreshold != null
      ? String(Math.round(field.verifyThreshold * 100))
      : "",
  );

  useEffect(() => {
    setMinLengthDraft(field?.minLength != null ? String(field.minLength) : "");
    setMaxLengthDraft(field?.maxLength != null ? String(field.maxLength) : "");
    setMinValueDraft(field?.minValue != null ? String(field.minValue) : "");
    setMaxValueDraft(field?.maxValue != null ? String(field.maxValue) : "");
    setRegexDraft(field?.regexPattern ?? "");
    setVerifyThresholdDraft(
      field?.verifyThreshold != null
        ? String(Math.round(field.verifyThreshold * 100))
        : "",
    );
  }, [
    field?.fieldDefinitionId,
    field?.minLength,
    field?.maxLength,
    field?.minValue,
    field?.maxValue,
    field?.regexPattern,
    field?.verifyThreshold,
  ]);

  if (!field || !content) return null;

  const isText = field.dataType === "text";
  const isNumeric =
    field.dataType === "number" ||
    field.dataType === "decimal" ||
    field.dataType === "currency";

  function toggleRequired(next: boolean) {
    if (!field) return;
    // Lane C Part 2 — set the flag only; the required validation is derived
    // on read from `isRequired`. No validate behavior is synthesized.
    updateField(field.name, { isRequired: next });
  }

  function toggleReadOnly(next: boolean) {
    if (!field) return;
    updateField(field.name, { readOnly: next });
  }

  function commitVerifyThreshold() {
    if (!field) return;
    const raw = verifyThresholdDraft.trim();
    let next: number | null;
    if (raw === "") {
      next = null;
    } else {
      const pct = Math.min(100, Math.max(0, parseInt(raw, 10)));
      next = Number.isNaN(pct) ? null : pct / 100;
    }
    if (next === (field.verifyThreshold ?? null)) return;
    updateField(field.name, { verifyThreshold: next });
  }

  function commitMinLength() {
    if (!field) return;
    const next = emptyToUndefinedNumber(minLengthDraft);
    if (next === field.minLength) return;
    updateField(field.name, { minLength: next });
  }
  function commitMaxLength() {
    if (!field) return;
    const next = emptyToUndefinedNumber(maxLengthDraft);
    if (next === field.maxLength) return;
    updateField(field.name, { maxLength: next });
  }
  function commitMinValue() {
    if (!field) return;
    const next = emptyToUndefinedNumber(minValueDraft);
    if (next === field.minValue) return;
    updateField(field.name, { minValue: next });
  }
  function commitMaxValue() {
    if (!field) return;
    const next = emptyToUndefinedNumber(maxValueDraft);
    if (next === field.maxValue) return;
    updateField(field.name, { maxValue: next });
  }

  function commitRegex() {
    if (!field) return;
    const trimmed = regexDraft.trim();
    const next = trimmed === "" ? undefined : trimmed;
    if (next === field.regexPattern) return;
    // R-115: typing a custom regex auto-switches the preset to "custom" so the
    // two controls stay in sync. Clearing the regex resets back to "any" so the
    // preset doesn't get stranded on "custom" with no pattern.
    const nextMode = next != null ? "custom" : "any";
    updateField(field.name, { regexPattern: next, allowedCharsMode: nextMode });
  }

  function commitAllowedCharsMode(next: NonNullable<FieldContent["allowedCharsMode"]>) {
    if (!field) return;
    if (next === "custom") {
      // Preserve current regexPattern.
      updateField(field.name, { allowedCharsMode: "custom" });
      return;
    }
    const presetRegex = ALLOWED_CHARS_MODE_WRITE[next];
    updateField(field.name, {
      allowedCharsMode: next,
      regexPattern: presetRegex,
    });
  }

  // Inline errors
  const lengthBoundsError =
    field.minLength != null && field.maxLength != null && field.minLength > field.maxLength
      ? "Min length is greater than max length."
      : null;
  const valueBoundsError =
    field.minValue != null && field.maxValue != null && field.minValue > field.maxValue
      ? "Min value is greater than max value."
      : null;
  const regexCompileError =
    field.regexPattern && field.regexPattern.length > 0
      ? tryCompileRegex(field.regexPattern)
      : null;

  return (
    <section className="field-section" aria-label="Validation">
      <h3 className="field-section__title">Validation</h3>

      <FieldCheckboxRow
        id="val-required"
        checked={field.isRequired ?? false}
        label="Required"
        hint="Empty values fail validation during keying."
        onChange={toggleRequired}
      />

      {isText && (
        <>
          <FieldRow
            htmlFor="val-min-length"
            label="Min length"
            hint="Empty = no minimum."
            error={lengthBoundsError}
          >
            <input
              id="val-min-length"
              type="number"
              min={0}
              className="field-section__input"
              value={minLengthDraft}
              onChange={(e) => setMinLengthDraft(e.target.value)}
              onBlur={commitMinLength}
              onKeyDown={blurOnEnter}
            />
          </FieldRow>

          <FieldRow
            htmlFor="val-max-length"
            label="Max length"
            hint="Empty = no maximum."
          >
            <input
              id="val-max-length"
              type="number"
              min={0}
              className="field-section__input"
              value={maxLengthDraft}
              onChange={(e) => setMaxLengthDraft(e.target.value)}
              onBlur={commitMaxLength}
              onKeyDown={blurOnEnter}
            />
          </FieldRow>
        </>
      )}

      {isNumeric && (
        <>
          <FieldRow
            htmlFor="val-min-value"
            label="Min value"
            hint="Empty = no minimum."
            error={valueBoundsError}
          >
            <input
              id="val-min-value"
              type="number"
              className="field-section__input"
              value={minValueDraft}
              onChange={(e) => setMinValueDraft(e.target.value)}
              onBlur={commitMinValue}
              onKeyDown={blurOnEnter}
            />
          </FieldRow>

          <FieldRow
            htmlFor="val-max-value"
            label="Max value"
            hint="Empty = no maximum."
          >
            <input
              id="val-max-value"
              type="number"
              className="field-section__input"
              value={maxValueDraft}
              onChange={(e) => setMaxValueDraft(e.target.value)}
              onBlur={commitMaxValue}
              onKeyDown={blurOnEnter}
            />
          </FieldRow>
        </>
      )}

      {isText && (
        <>
          <FieldRow
            htmlFor="val-allowed-chars"
            label="Allowed characters"
            hint="Pick a preset or use custom regex below."
          >
            <select
              id="val-allowed-chars"
              className="field-section__input"
              value={field.allowedCharsMode ?? "any"}
              onChange={(e) =>
                commitAllowedCharsMode(
                  e.target.value as NonNullable<FieldContent["allowedCharsMode"]>,
                )
              }
            >
              {ALLOWED_CHARS_MODE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FieldRow>

          <FieldRow
            htmlFor="val-regex"
            label="Regex pattern"
            hint={
              <>
                JavaScript regex (no slashes, no flags). Example:{" "}
                <code>^[A-Z]{"{"}3{"}"}\d{"{"}4{"}"}$</code> for 3 letters + 4 digits
                (e.g., <code>ABC1234</code>).
              </>
            }
            error={regexCompileError ? `Invalid regex: ${regexCompileError}` : null}
          >
            <input
              id="val-regex"
              type="text"
              className="field-section__input"
              value={regexDraft}
              onChange={(e) => setRegexDraft(e.target.value)}
              onBlur={commitRegex}
              onKeyDown={blurOnEnter}
              placeholder="^[A-Z0-9-]+$"
            />
          </FieldRow>
        </>
      )}

      <FieldCheckboxRow
        id="val-readonly"
        checked={field.readOnly ?? false}
        label="Read-only"
        hint="Verification station renders this field as read-only; export refuses mutations."
        onChange={toggleReadOnly}
      />

      {/* R-128 / Phase 1.5 WU-3 — per-field confidence floor (override). */}
      {/* Re-uses the existing `verifyThreshold` column; relocated from        */}
      {/* BehaviorSection so it lives with the other validation properties.    */}
      <FieldRow
        htmlFor="val-confidence-floor"
        label="Confidence floor (override) %"
        hint="OCR confidence at/above which this field is auto-verified (skipped at the verification station); below it the field stops for review. Blank = inherit the job type's confidence floor. 100 = always stop for review."
      >
        <input
          id="val-confidence-floor"
          type="number"
          min={0}
          max={100}
          step={1}
          className="field-section__input"
          placeholder="Inherit"
          value={verifyThresholdDraft}
          onChange={(e) => setVerifyThresholdDraft(e.target.value)}
          onBlur={commitVerifyThreshold}
          onKeyDown={blurOnEnter}
        />
      </FieldRow>
    </section>
  );
}
